import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Provider, useSelector } from 'react-redux';
import { useEffect, useRef } from 'react';
import { store, RootState } from './store';
import { ThemeProvider } from './hooks/useTheme';
import { I18nProvider } from './hooks/useI18n';
import './App.css';
import LoginModern from './pages/LoginModern';
import SignupModern from './pages/SignupModern';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Library from './pages/Library';
import Categories from './pages/Categories';
import Favorites from './pages/Favorites';
import Settings from './pages/Settings';
import Trash from './pages/Trash';
import Notifications from './pages/Notifications';
import AuditTrail from './pages/AuditTrail';
import UserManagement from './pages/UserManagement';
import Workspaces from './pages/Workspaces';
import WorkspaceSettings from './pages/WorkspaceSettings';
import OrganizationDetail from './pages/OrganizationDetail';
import TeamDetail from './pages/TeamDetail';
import TeamSettings from './pages/TeamSettings';
import Homepage from './pages/Homepage';
import SubscriptionHub from './pages/SubscriptionHub';
import Collections from './pages/Collections';
import CalendarPage from './pages/Calendar';
import NotificationSettings from './pages/NotificationSettings';
import FeedbackPage from './pages/FeedbackPage';
import FeedbackDetailPage from './pages/FeedbackDetailPage';
import AIRemindersPage from './pages/AIRemindersPage';
import Accounting from './pages/Accounting';
import SuperAdmin from './pages/SuperAdmin';
import AiAssistant from './pages/AiAssistant';
import DuplicateFinder from './pages/DuplicateFinder';
import HiddenFolders from './pages/HiddenFolders';
import GoogleCallback from './pages/GoogleCallback';
import DataTransfer from './pages/DataTransfer';
import DashboardLayout from './components/DashboardLayout';
import { RBACProvider } from './hooks/useRBAC';
import TutorialOverlay, { TutorialReplayButton } from './components/TutorialOverlay';
import { useInactivityLogout } from './hooks/useInactivityLogout';
import { initTelemetry, trackPageView, syncTelemetryOptOut, shutdownTelemetry } from './services/telemetryService';
import CookieConsent from './components/CookieConsent';
import {
  ContactPage,
  PressPage,
  DocsPage,
  ApiDocsPage,
  BlogPage,
  HelpPage,
  StatusPage,
  IntegrationsPage,
  PrivacyPage,
  TermsPage,
  CookiePolicyPage,
} from './pages/InfoPages';

// Protected Route wrapper
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { token } = useSelector((state: RootState) => state.auth);
  return token ? <>{children}</> : <Navigate to="/home" replace />;
};

// Calls useInactivityLogout (needs Router context via useNavigate)
const InactivityHandler: React.FC = () => {
  useInactivityLogout();
  return null;
};

// Track page views on route changes (must be inside Router)
const TelemetryTracker: React.FC = () => {
  const location = useLocation();
  const prevPath = useRef('');
  useEffect(() => {
    if (location.pathname !== prevPath.current) {
      trackPageView(location.pathname);
      prevPath.current = location.pathname;
    }
  }, [location.pathname]);
  return null;
};

// Shown only when user is authenticated — mounts/unmounts with auth state
const AuthenticatedFeatures: React.FC = () => {
  const { token } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    if (token) {
      initTelemetry();
      syncTelemetryOptOut();
    }
    return () => {
      if (!token) shutdownTelemetry();
    };
  }, [token]);

  if (!token) return null;
  return (
    <>
      <InactivityHandler />
      <TutorialOverlay onComplete={() => {}} />
      <TutorialReplayButton />
    </>
  );
};

function App() {
  // Apply saved accessibility preferences on app start
  useEffect(() => {
    document.body.classList.add('formal-ui');
    if (localStorage.getItem('reduceMotion') === 'true') {
      document.body.classList.add('reduce-motion');
    }
    if (localStorage.getItem('prefersTouch') === 'true') {
      document.body.classList.add('prefer-touch');
    }
  }, []);

  useEffect(() => {
    const syncLogout = (event: StorageEvent) => {
      if (event.key === 'elvoaq_logout_event' && window.location.pathname !== '/login') {
        window.location.replace('/login');
      }
    };

    window.addEventListener('storage', syncLogout);
    return () => window.removeEventListener('storage', syncLogout);
  }, []);

  return (
    <Provider store={store}>
      <ThemeProvider>
        <I18nProvider>
          <RBACProvider>
            <Router>
              <TelemetryTracker />
              <AuthenticatedFeatures />
              <CookieConsent />
              <Routes>
            <Route path="/home" element={<Homepage />} />
            <Route path="/login" element={<LoginModern />} />
            <Route path="/signup" element={<SignupModern />} />
            <Route path="/auth/google/callback" element={<GoogleCallback />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/pricing" element={<Navigate to="/subscription" replace />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/press" element={<PressPage />} />
            <Route path="/docs" element={<DocsPage />} />
            <Route path="/api-docs" element={<ApiDocsPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/help" element={<HelpPage />} />
            <Route path="/status" element={<StatusPage />} />
            <Route path="/integrations" element={<IntegrationsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/cookie-policy" element={<CookiePolicyPage />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Dashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/library"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Library />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/categories"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Categories />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/favorites"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Favorites />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/trash"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Trash />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Notifications />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Settings />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          {/* Alias for audit trail */}
          <Route
            path="/history"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <AuditTrail />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/audit"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <AuditTrail />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/workspaces"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Workspaces />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/workspaces/:id/settings"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <WorkspaceSettings />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/organizations/:id"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <OrganizationDetail />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/organizations/:id/settings"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <WorkspaceSettings />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/teams/:id"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <TeamDetail />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/teams/:id/settings"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <TeamSettings />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/notification-settings"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <NotificationSettings />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/storage"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <SubscriptionHub />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscription"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <SubscriptionHub />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/collections"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Collections />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/calendar"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <CalendarPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <UserManagement />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/feedback"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <FeedbackPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/feedback/:id"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <FeedbackDetailPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/reminders"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <AIRemindersPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounting"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Accounting />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/ai-assistant"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <AiAssistant />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/duplicate-finder"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <DuplicateFinder />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/hidden-folders"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <HiddenFolders />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/data-transfer"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <DataTransfer />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          {/* Platform Admin Console — completely separate from customer UI */}
          <Route
            path="/platform-admin"
            element={
              <ProtectedRoute>
                <SuperAdmin />
              </ProtectedRoute>
            }
          />
        </Routes>
      </Router>
      </RBACProvider>
    </I18nProvider>
    </ThemeProvider>
    </Provider>
  );
}

export default App;

